import axios from 'axios';
import { getToken } from '../utils/auth';
import { DEFAULT_ROUTE, SERVER_ROUTE } from '../routes/api/api_routes';

// Create axios instance with default config
const api = axios.create({
    baseURL: SERVER_ROUTE,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add request interceptor
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `JWT ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// User API calls
export const userAPI = {
    register: (data) => api.post('users/', data),
    login: (data) => api.post('login/', data),
    logout: () => api.post('logout/'),
    resetPassword: (data) => api.post('password-reset/', data),
    getProfile: () => api.get('users/me/'),
    updateProfile: (data) => api.put('users/me/', data)
};

// Quiz API calls
export const quizAPI = {
    getQuizzes: () => api.get('quizzprofile/'),
    getQuiz: (id) => api.get(`quizzprofile/${id}/`),
    createQuiz: (data) => api.post('quizzprofile/', data),
    updateQuiz: (id, data) => api.put(`quizzprofile/${id}/`, data),
    deleteQuiz: (id) => api.delete(`quizzprofile/${id}/`),
    getQuizResult: (id) => api.get(`quizzprofile/resultat/${id}/`)
};

// Question API calls
export const questionAPI = {
    getQuestions: () => api.get('question/'),
    getQuestion: (id) => api.get(`question/${id}/`),
    createQuestion: (data) => api.post('question/', data),
    updateQuestion: (id, data) => api.put(`question/${id}/`, data),
    deleteQuestion: (id) => api.delete(`question/${id}/`)
};

// Challenge API calls
export const challengeAPI = {
    getChallenges: () => api.get('challengeprofile/'),
    getChallenge: (id) => api.get(`challengeprofile/${id}/`),
    createChallenge: (data) => api.post('challengeprofile/', data),
    updateChallenge: (id, data) => api.put(`challengeprofile/${id}/`, data),
    deleteChallenge: (id) => api.delete(`challengeprofile/${id}/`),
    getChallengeResult: (id) => api.get(`challengeprofile/resultat/${id}/`)
};

// Tutorial API calls
export const tutorialAPI = {
    getTutorials: () => api.get('tutorial/'),
    getTutorial: (id) => api.get(`tutorial/${id}/`),
    createTutorial: (data) => api.post('tutorial/', data),
    updateTutorial: (id, data) => api.put(`tutorial/${id}/`, data),
    deleteTutorial: (id) => api.delete(`tutorial/${id}/`)
};

// Progress API calls
export const progressAPI = {
    getProgress: () => api.get('progress/'),
    updateProgress: (data) => api.put('progress/', data),
    getQuizProgress: (quizId) => api.get(`progress/quiz/${quizId}/`),
    getChallengeProgress: (challengeId) => api.get(`progress/challenge/${challengeId}/`)
};

// Error handling
export const handleApiError = (error) => {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('API Error:', error.response.data);
        return error.response.data;
    } else if (error.request) {
        // The request was made but no response was received
        console.error('API Request Error:', error.request);
        return { error: 'No response from server' };
    } else {
        // Something happened in setting up the request that triggered an Error
        console.error('API Setup Error:', error.message);
        return { error: error.message };
    }
};

export default api; 